import { useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { trackFilterEvent } from '@plp/Components/Filters/Filters.tracking';
import type { Item } from '@plp/Components/MultiSelect/MultiSelect.entity';

import { useUpdateFilters } from './UseUpdateFilters';

export function useSearchFilter({
  filterId,
  data,
  applyButton,
}: {
  filterId: FilterKey;
  data: Item[];
  applyButton?: boolean;
}) {
  const { updateFilters } = useUpdateFilters();
  const filtersList = useAtomValue(filtersListAtom);
  const filter = getFilterFromArray(filterId, filtersList);
  const [searchValue, setSearchValue] = useState('');
  const [pendingSelectedOptions, setPendingSelectedOptions] = useState(
    new Set(filter?.value || []),
  );
  const selectedOptions = new Set(filter?.value || []);

  const handleSelect = (id: string, isChecked: boolean) => {
    const selected = applyButton ? new Set(pendingSelectedOptions) : new Set(selectedOptions);

    if (isChecked) {
      selected.add(id);
    } else {
      selected.delete(id);
    }

    if (applyButton) {
      setPendingSelectedOptions(selected);
    } else {
      const filterValue = Array.from(selected);
      trackFilterEvent(filterId, filterValue, filtersList);
      updateFilters([{ id: filterId, value: filterValue }]);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const allOptions = data.map(option => ({
    ...option,
    disabled: !selectedOptions.has(option.id) && option.count === 0,
    selected: applyButton ? pendingSelectedOptions.has(option.id) : selectedOptions.has(option.id),
  }));

  const filteredOptions = allOptions.filter(option => {
    return option.label.toLowerCase().includes(searchValue.toLowerCase());
  });

  const resetFilter = () => {
    if (applyButton) {
      setPendingSelectedOptions(new Set());
    } else {
      updateFilters([{ id: filterId, value: [] }]);
    }
  };

  const handleApply = useCallback(() => {
    const filterValue = Array.from(pendingSelectedOptions);
    trackFilterEvent(filterId, filterValue, filtersList);
    updateFilters([{ id: filterId, value: filterValue }]);
  }, [pendingSelectedOptions, filterId, filtersList, updateFilters]);

  return {
    handleSelect,
    handleSearchChange,
    searchValue,
    filteredOptions,
    resetFilter,
    selectedOptions: applyButton ? pendingSelectedOptions : selectedOptions,
    allOptions,
    isSearching: !!searchValue,
    handleApply,
  };
}
